/* Original Littlebird motion, adapted from the source site's Motion components.
   Content stays readable without JavaScript and with reduced motion enabled. */
(() => {
  const preference = matchMedia('(prefers-reduced-motion: reduce)');
  const managed = new Map();
  const parallax = new Set();
  const marquees = new Set();
  let frame = 0;
  let lastFrame = 0;
  let lastScroll = scrollY;
  let lastNavTime = 0;

  const clamp = (value, min = 0, max = 1) => Math.max(min, Math.min(max, value));
  const all = (root, selector) => [ ...(root.matches?.(selector) ? [root] : []), ...root.querySelectorAll(selector) ];
  const register = (element, setup) => {
    if (!managed.has(element)) managed.set(element, setup(element));
  };
  const requestTick = () => { if (!frame && !document.hidden) frame = requestAnimationFrame(tick); };

  // The same critically damped text reveals and softer button spring as the source.
  function spring(time, stiffness, damping, mass = 1) {
    const rate = damping / (2 * mass);
    const frequency = Math.sqrt(stiffness / mass);
    if (Math.abs(rate - frequency) < 0.0001) return 1 - Math.exp(-rate * time) * (1 + rate * time);
    if (rate < frequency) {
      const damped = Math.sqrt(frequency * frequency - rate * rate);
      return 1 - Math.exp(-rate * time) * (Math.cos(damped * time) + rate / damped * Math.sin(damped * time));
    }
    const discriminant = Math.sqrt(rate * rate - frequency * frequency);
    const slow = -rate + discriminant, fast = -rate - discriminant;
    return 1 - (fast * Math.exp(slow * time) - slow * Math.exp(fast * time)) / (fast - slow);
  }

  function springFrames(distance, duration, stiffness, damping, fromOpacity = 0, toOpacity = 1) {
    return Array.from({ length: 61 }, (_, index) => {
      const progress = index === 60 ? 1 : spring(index / 60 * duration / 1000, stiffness, damping);
      const value = { opacity: fromOpacity + (toOpacity - fromOpacity) * clamp(progress), offset: index / 60 };
      if (distance !== null) value.transform = `translate3d(0,${distance * (1 - progress)}px,0)`;
      return value;
    });
  }

  function reveal(element) {
    const kind = element.dataset.lbReveal;
    const image = kind === 'image' || kind === 'image-wide';
    const story = kind === 'story';
    const mobileReview = element.hasAttribute('data-lb-mobile-review') && matchMedia('(max-width: 809px)').matches;
    const threshold = image ? 0.5 : story ? 0.08 : mobileReview ? 0.35 : 0;
    const distance = { heading: 7, body: 10, buttons: 10, image: 4, 'image-wide': 0, story: 14 }[kind] || 0;
    const delay = { heading: 100, body: 75, buttons: 800 }[kind] || 0;
    let animation;
    let complete = false;
    const show = (immediate = false) => {
      if (complete && !immediate) return;
      complete = true;
      observer.disconnect();
      animation?.cancel();
      element.dataset.lbRevealState = 'shown';
      if (immediate) return;
      const keyframes = image || story
        ? [{ opacity: 0, transform: `translate3d(0,${distance}px,0)` }, { opacity: 1, transform: 'translate3d(0,0,0)' }]
        : springFrames(distance, 1000, 100, kind === 'buttons' ? 16 : 20);
      animation = element.animate(keyframes, { duration: story ? 600 : image ? 400 : 1000, delay, easing: story ? 'cubic-bezier(.25,1,.5,1)' : image ? 'ease-in' : 'linear', fill: 'both' });
      animation.onfinish = () => { animation.cancel(); animation = null; };
    };
    const observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting && entry.intersectionRatio >= threshold)) show();
    }, { threshold, rootMargin: mobileReview ? '0px 0px -8% 0px' : kind === 'image-wide' ? '4px 0px 0px' : '0px' });
    element.dataset.lbRevealState = 'pending';
    observer.observe(element);
    const focus = () => show(true);
    element.addEventListener('focusin', focus);
    return () => { observer.disconnect(); animation?.cancel(); element.removeEventListener('focusin', focus); delete element.dataset.lbRevealState; };
  }

  function words(element) {
    // Split text nodes, preserving inline font/color spans, links, and line breaks.
    const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
    const nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);
    const spans = [];
    nodes.forEach(node => {
      const fragment = document.createDocumentFragment();
      node.textContent.split(/(\s+)/).forEach(text => {
        if (!text) return;
        if (/^\s+$/.test(text)) { fragment.append(document.createTextNode(text)); return; }
        const span = document.createElement('span');
        span.className = 'lb-reveal-word'; span.textContent = text;
        spans.push(span); fragment.append(span);
      });
      node.replaceWith(fragment);
    });
    const observers = [], animations = new Map();
    spans.forEach((span, index) => {
      // Original per-word viewport margins: 10% through 60%, with amount: 1.
      const margin = 10 + 50 * index / spans.length;
      let visible = false;
      const observer = new IntersectionObserver(entries => {
        const next = entries[0].isIntersecting && entries[0].intersectionRatio >= 1;
        if (visible === next) return;
        const opacity = Number.parseFloat(getComputedStyle(span).opacity);
        visible = next;
        animations.get(span)?.cancel();
        span.style.opacity = next ? '1' : '0.2';
        const animation = span.animate(springFrames(null, 800, 500, 60, opacity, next ? 1 : 0.2), { duration: 800, fill: 'both', easing: 'linear' });
        animations.set(span, animation);
        animation.onfinish = () => { animation.cancel(); animations.delete(span); };
      }, { threshold: 1, rootMargin: `${margin}% 0px -${margin}% 0px` });
      observer.observe(span); observers.push(observer);
    });
    return () => {
      observers.forEach(observer => observer.disconnect());
      animations.forEach(animation => animation.cancel());
      spans.forEach(span => span.replaceWith(document.createTextNode(span.textContent)));
      element.normalize();
    };
  }

  function homeEntrance(element) {
    const heading = element.querySelector('[data-lb-hero-part="heading"]');
    const body = element.querySelector('[data-lb-hero-part="body"]');
    const buttons = element.querySelector('[data-lb-hero-part="buttons"]');
    if (!heading) return () => {};
    const originalHeading = heading.innerHTML;
    const animations = [];
    let fontWait;
    let complete = false;
    element.dataset.lbHeroState = 'pending';
    const finish = () => {
      if (complete) return;
      complete = true;
      clearTimeout(fontWait);
      animations.forEach(animation => animation.cancel());
      heading.innerHTML = originalHeading;
      delete element.dataset.lbHeroState;
      element.removeEventListener('focusin', finish);
      window.removeEventListener('resize', finish);
    };
    const start = () => {
      if (complete || !element.isConnected) return;
      clearTimeout(fontWait);
      // Measure the naturally wrapped lines; keep the green accent and no-wrap phrases.
      const walker = document.createTreeWalker(heading, NodeFilter.SHOW_TEXT);
      const nodes = [];
      while (walker.nextNode()) nodes.push(walker.currentNode);
      nodes.forEach(node => {
        const fragment = document.createDocumentFragment();
        node.textContent.split(/(\s+)/).forEach(text => {
          if (!text) return;
          if (/^\s+$/.test(text)) fragment.append(document.createTextNode(text));
          else {
            const word = document.createElement('span');
            word.className = 'lb-hero-word';
            word.textContent = text;
            fragment.append(word);
          }
        });
        node.replaceWith(fragment);
      });
      const words = [...heading.querySelectorAll('.lb-hero-word')];
      const lines = [];
      const positions = words.map(word => {
        const top = word.getBoundingClientRect().top;
        let line = lines.findIndex(value => Math.abs(value - top) < 2);
        if (line < 0) line = lines.push(top) - 1;
        return line;
      });
      const rise = (target, lift, delay) => {
        animations.push(target.animate([
          {opacity: 0, transform: `translateY(${lift}px)`},
          {opacity: 1, transform: 'translateY(0)'}
        ], {duration: 1200, delay, easing: 'cubic-bezier(.25,1,.5,1)', fill: 'both'}));
      };
      words.forEach((word, index) => {
        word.dataset.lbHeroLine = String(positions[index]);
        rise(word, 30, 100 + positions[index] * 100);
      });
      if (body) rise(body, 20, 320);
      if (buttons) rise(buttons, 20, 540);
      element.dataset.lbHeroState = 'entering';
      Promise.all(animations.map(animation => animation.finished)).then(finish, () => {});
    };
    element.addEventListener('focusin', finish);
    window.addEventListener('resize', finish, {passive: true});
    // Let fonts settle briefly without leaving the hero hidden on a slow connection.
    Promise.race([
      Promise.resolve(document.fonts?.ready),
      new Promise(resolve => { fontWait = setTimeout(resolve, 300); })
    ]).then(start).catch(finish);
    return finish;
  }

  function floatImage(element) {
    const kind = element.dataset.lbParallax;
    const state = { element, kind, target: element.closest('section'), visible: false, position: kind === 'phone-right' ? -75 : 75, velocity: 0 };
    if (!state.target) return () => {};
    const observer = new IntersectionObserver(entries => { state.visible = entries[0].isIntersecting; requestTick(); }, { rootMargin: '150px 0px' });
    observer.observe(state.target);
    parallax.add(state);
    return () => { observer.disconnect(); parallax.delete(state); element.style.removeProperty('transform'); };
  }

  function marquee(element) {
    const viewport = element.querySelector('[data-slot="carousel-content"]');
    const track = viewport?.firstElementChild;
    if (!track) return () => {};
    const originals = [...track.children];
    const state = { viewport, visible: false, period: 0, active: false, interacting: false, hovered: false, focused: false, paused: false };
    const clones = [];
    const button = document.createElement('button');
    button.className = 'lb-marquee-toggle'; button.type = 'button'; button.textContent = 'Pause logos';
    button.setAttribute('aria-label', 'Pause press logos');
    button.hidden = true;
    element.querySelector('.logotypes-wrapper').append(button);
    const measure = () => {
      clones.splice(0).forEach(clone => clone.remove());
      element.classList.remove('lb-marquee-running');
      // Embla's original 100px gap and 0.8px/frame auto-scroll, only if overflowing.
      const period = originals.reduce((width, item) => width + item.getBoundingClientRect().width, 0);
      const leadingMargin = Number.parseFloat(getComputedStyle(track).marginLeft) || 0;
      state.active = period + leadingMargin > viewport.clientWidth + 1;
      state.period = 0;
      button.hidden = !state.active;
      if (state.active) {
        element.classList.add('lb-marquee-running');
        state.period = period;
        if (state.period > 0) {
          const copies = Math.ceil(viewport.clientWidth / state.period) + 1;
          for (let repeat = 0; repeat < copies; repeat++) originals.forEach(item => {
            const clone = item.cloneNode(true); clone.setAttribute('aria-hidden', 'true'); clone.setAttribute('inert', '');
            clone.querySelectorAll('[id]').forEach(node => node.removeAttribute('id'));
            clone.querySelectorAll('img').forEach(image => { image.loading = 'eager'; image.alt = ''; });
            track.append(clone); clones.push(clone);
          });
          viewport.scrollLeft %= state.period;
        }
      } else viewport.scrollLeft = 0;
      requestTick();
    };
    const listeners = [];
    const listen = (target, event, fn) => { target.addEventListener(event, fn, { passive: true }); listeners.push(() => target.removeEventListener(event, fn)); };
    listen(viewport, 'pointerdown', () => { state.interacting = true; });
    listen(window, 'pointerup', () => { state.interacting = false; requestTick(); });
    listen(window, 'pointercancel', () => { state.interacting = false; requestTick(); });
    listen(element, 'mouseenter', () => { state.hovered = true; });
    listen(element, 'mouseleave', () => { state.hovered = false; requestTick(); });
    listen(element, 'focusin', () => { state.focused = true; });
    listen(element, 'focusout', event => { state.focused = element.contains(event.relatedTarget); requestTick(); });
    listen(button, 'click', () => { state.paused = !state.paused; button.textContent = state.paused ? 'Play logos' : 'Pause logos'; button.setAttribute('aria-label', state.paused ? 'Play press logos' : 'Pause press logos'); requestTick(); });
    const observer = new IntersectionObserver(entries => { state.visible = entries[0].isIntersecting; requestTick(); });
    observer.observe(element);
    const resize = new ResizeObserver(measure); resize.observe(viewport);
    originals.forEach(item => item.querySelectorAll('img').forEach(image => listen(image, 'load', measure)));
    marquees.add(state); measure();
    return () => { observer.disconnect(); resize.disconnect(); listeners.forEach(remove => remove()); clones.forEach(clone => clone.remove()); button.remove(); element.classList.remove('lb-marquee-running'); viewport.scrollLeft = 0; marquees.delete(state); };
  }

  function tick(now) {
    frame = 0;
    const elapsed = Math.min(lastFrame ? now - lastFrame : 16.667, 50);
    lastFrame = now;
    let again = false;
    const bounds = new Map();
    parallax.forEach(state => { if (state.visible && !bounds.has(state.target)) bounds.set(state.target, state.target.getBoundingClientRect()); });
    parallax.forEach(state => {
      if (!state.visible) return;
      const rect = bounds.get(state.target);
      if (state.kind === 'image') {
        // Source offset ["end start", "start end"], mapped from 100 to -100px.
        const progress = clamp((innerHeight - rect.top) / (innerHeight + rect.height));
        state.element.style.transform = `translate3d(0,${-100 + 200 * progress}px,0)`;
      } else if (innerWidth >= 810) {
        const progress = clamp((innerHeight - rect.top) / innerHeight);
        const destination = (75 - 10 * progress) * (state.kind === 'phone-right' ? -1 : 1);
        // Original phone spring: stiffness 50, damping 15, mass 1.5.
        const steps = Math.ceil(elapsed / 8), delta = elapsed / steps / 1000;
        for (let step = 0; step < steps; step++) {
          state.velocity += (-50 * (state.position - destination) - 15 * state.velocity) / 1.5 * delta;
          state.position += state.velocity * delta;
        }
        const settling = Math.abs(state.position - destination) > 0.002 || Math.abs(state.velocity) > 0.002;
        if (!settling) { state.position = destination; state.velocity = 0; }
        state.element.style.transform = `translate3d(0,${state.position}%,0)`;
        again ||= settling;
      }
    });
    marquees.forEach(state => {
      if (!state.visible || !state.active || !state.period || state.interacting || state.hovered || state.focused || state.paused) return;
      state.viewport.scrollLeft = (state.viewport.scrollLeft + 48 * elapsed / 1000) % state.period;
      again = true;
    });
    // Original navigation: hide while scrolling down, show when scrolling up.
    if (now - lastNavTime >= 150 || Math.abs(scrollY - lastScroll) < 1) {
      const header = document.querySelector('.lb-header');
      const engaged = header?.contains(document.activeElement) || header?.querySelector('[aria-expanded="true"]');
      const hidden = !preference.matches && !engaged && scrollY > lastScroll && scrollY > 64;
      if (scrollY !== lastScroll || engaged || preference.matches) document.documentElement.style.setProperty('--nav-hide', hidden ? '1' : '0');
      lastScroll = scrollY; lastNavTime = now;
    } else if (scrollY !== lastScroll) again = true;
    if (again) requestTick();
  }

  function init(root = document) {
    if (preference.matches || !('IntersectionObserver' in window) || !Element.prototype.animate) return;
    all(root, '[data-lb-home-entrance]').forEach(element => register(element, homeEntrance));
    all(root, '[data-lb-reveal]').forEach(element => register(element, reveal));
    all(root, '[data-lb-word-reveal]').forEach(element => register(element, words));
    all(root, '[data-lb-parallax]').forEach(element => register(element, floatImage));
    if ('ResizeObserver' in window) all(root, '[data-lb-marquee]').forEach(element => register(element, marquee));
    requestTick();
  }
  function destroy(root = document) {
    managed.forEach((cleanup, element) => { if (root === document || root === element || root.contains(element)) { cleanup(); managed.delete(element); } });
  }
  init();
  // Back/forward cache restores the finished DOM, so arm the home entrance again.
  // Normal navigations and refreshes already get a fresh entrance from init().
  addEventListener('pageshow', event => {
    if (!event.persisted || preference.matches) return;
    all(document, '[data-lb-home-entrance]').forEach(element => {
      managed.get(element)?.();
      managed.delete(element);
      const rect = element.getBoundingClientRect();
      if (rect.bottom > 0 && rect.top < innerHeight) register(element, homeEntrance);
    });
  });
  addEventListener('scroll', requestTick, { passive: true });
  addEventListener('resize', requestTick, { passive: true });
  document.addEventListener('visibilitychange', () => { lastFrame = 0; if (document.hidden) { cancelAnimationFrame(frame); frame = 0; } else requestTick(); });
  document.addEventListener('focusin', event => { if (event.target.closest('.lb-header')) { document.documentElement.style.setProperty('--nav-hide', '0'); requestTick(); } });
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => destroy(event.target));
  document.addEventListener('littlebird:content:load', event => init(event.target));
  document.addEventListener('littlebird:content:unload', event => destroy(event.target));
  document.addEventListener('shopify:section:select', event => { all(event.target, '[data-lb-reveal-state]').forEach(element => { managed.get(element)?.(); managed.delete(element); }); });
  preference.addEventListener('change', () => { destroy(); document.documentElement.style.setProperty('--nav-hide', '0'); init(); });
})();
